import { TemplateQueryIDs, TemplateQueryInfo } from "routes/dashboard/DashboardTemplateQueryPage";
import { InfegyAccountStub, InfegyUserStub } from "system/InfegyUser";

export enum DashboardWidgetSpacing {
  MINIMAL = "MINIMAL",
  COMPACT = "COMPACT",
  COMFORTABLE = "COMFORTABLE",
  SPACIOUS = "SPACIOUS"
}

export const DashboardDataDefaults = {
  FullColumnCount: 6,
  MobileColumnCount: 1,
  WidgetSpacing: DashboardWidgetSpacing.COMFORTABLE
};

export interface DashboardData {
  pages: Array<string>;

  defaultWidgetQuery?: string;
  defaultDashboardQuery?: string;

  // How often the dashboard should automatically refresh in seconds.
  autoRefreshInterval?: number;

  // This contains a timestamp of when the dashboard queries were last updated.
  // This is used for caching, and any cached data older than this timestamp
  // should be considered stale and will be reloaded.
  dashboardCacheInvalidatedAt?: string;

  fullColumnCount?: number;
  widgetSpacing?: DashboardWidgetSpacing;
}

export interface DashboardListResponse {
  display_name?: string;
  endpoint?: string;
  permissions?: Array<string>;
  schema?: any;
  schema_groups?: any;
  sub_resources?: Array<any>;

  pagination?: {
    total: number;
    page: number;
    per_page: number;
    cursor?: string;
  };

  output: Array<DashboardDocument>;
}


export interface DashboardDocument {
  id: string;

  user_id: string;
  account_id: string;

  name: string;
  description?: string | null;

  _deleted: boolean;
  _deletable: boolean;
  deleted_at: string | null;

  created_at: string;
  updated_at: string;
  // The user has marked the dashboard as a favorite.
  is_favorite: boolean;
  // The last time the dashboard was viewed by anyone.
  last_viewed: string | null;
  // The last time the dashboard was viewed by the user.
  last_viewed_by_you: string | null;

  access: string;
  _modifiable: boolean;
  _owned_by_you: boolean;
  _is_shared: boolean;

  template_type: string | null;
  dataset_id: string | null;
  templateQueries?: Array<string>;

  data: DashboardData;

  // Frontend only to indicate if the dashboard is being loaded from a public link
  // with no user context.
  _is_public?: boolean;

  _user?: InfegyUserStub;
  _account?: InfegyAccountStub;
}

export interface TemplateQueryIDs {
  queryIds: Array<string>;
  globalQueryId?: string;
}

export function parseTemplateQueryIDs(queryIds: string | undefined): TemplateQueryIDs {
  const templateQueryIDs: TemplateQueryIDs = { queryIds: [] };
  if (!queryIds) {
    return templateQueryIDs;
  }
  const rawTemplateQueryIds = queryIds.split("-");
  // Check if we have a global query (designated by starting with "g_") and set it aside
  const globalQueryIndex = rawTemplateQueryIds.findIndex((queryId) => queryId.startsWith("g_"));
  if (globalQueryIndex >= 0) {
    templateQueryIDs.globalQueryId = rawTemplateQueryIds[globalQueryIndex].slice(2); // remove the "g_" prefix
    templateQueryIDs.queryIds = rawTemplateQueryIds.filter((_, index) => index !== globalQueryIndex);
  } else {
    templateQueryIDs.queryIds = rawTemplateQueryIds;
  }
  return templateQueryIDs;
}

export function assembleTemplateQueryIDString(queryIds?: Array<string>, globalQueryId?: string): string {
  const allQueryIds = globalQueryId ? [...(queryIds || []), `g_${globalQueryId}`] : (queryIds || []);
  return allQueryIds.join("-");
}

// Extracts the dashboard query IDs from the URL and returns them in a structured format.
export function parseTemplateQueryInfo(queryIds: string | undefined, templateQueryIndices: Array<number> | string | undefined, dashboard: DashboardDocument): TemplateQueryInfo {
  const templateQueryIds = parseTemplateQueryIDs(queryIds);

  if (!templateQueryIds.queryIds || templateQueryIds.queryIds.length === 0) {
    // No template query IDs provided, so check if we have a default dashboard query to use instead.
    if (dashboard?.data?.defaultDashboardQuery) {
      templateQueryIds.queryIds = [dashboard.data.defaultDashboardQuery];
    } else {
      return {queryIds: undefined, globalQueryId: templateQueryIds.globalQueryId} as TemplateQueryInfo;
    }
  }

  const dashboardURLQueryInfo = {
    templateQueryIds: templateQueryIds.queryIds,
    globalQueryId: templateQueryIds.globalQueryId,
    selectedTemplateQueryIndices: [],
  } as TemplateQueryInfo;

  if (templateQueryIndices === "all") {
    dashboardURLQueryInfo.selectedTemplateQueryIndices = templateQueryIds.queryIds.map((_:string, index :number) => index);
    return dashboardURLQueryInfo;
  }

  let localTemplateQueryIndices: Array<number> | undefined = undefined;
  if (typeof templateQueryIndices === "string") {
    localTemplateQueryIndices = templateQueryIndices.split(",").map(index => index.trim()).map(Number);
  } else if (Array.isArray(templateQueryIndices)) {
    localTemplateQueryIndices = templateQueryIndices.map(Number);
  }

  if (localTemplateQueryIndices === undefined || (Array.isArray(localTemplateQueryIndices) && localTemplateQueryIndices.length === 0)) {
    // No indices provided, if we aren't a template dashboard, we should just return the first query
    if (!dashboard.template_type) {
      dashboardURLQueryInfo.selectedTemplateQueryIndices = [0];
    } else {
      // Template dashboard with no indices provided, so we return undefined which will trigger the redirect to the first template query
      dashboardURLQueryInfo.selectedTemplateQueryIndices = undefined;
    }
    return dashboardURLQueryInfo;
  }

  const validIndices = localTemplateQueryIndices.map(Number).filter(index => typeof index === "number" && !isNaN(index));

  // If any of the provided template query indices are not a number or is out of bounds,
  // we should use the valid indices only, or default to undefined if none are valid.
  if (!validIndices || validIndices.length === 0) {
    dashboardURLQueryInfo.selectedTemplateQueryIndices = undefined;
  } else {
    dashboardURLQueryInfo.selectedTemplateQueryIndices = validIndices;
  }

  return dashboardURLQueryInfo;
}